import { isoDateSchema, type TaskListFilter, type TaskSummary } from '@pm-tool/shared';
import { filtersOf, parseListSearch } from '@/features/tasks/list-search';

/**
 * URL state of the calendar (TASK-10): the list filters without due date (the calendar sets the
 * range itself), the view (month by default), the date shown (today by default) and the open task.
 */
export type CalendarSearch = Omit<TaskListFilter, 'due' | 'dueFrom' | 'dueTo'> & {
  view?: 'week';
  date?: string;
  task?: string;
};

export function parseCalendarSearch(raw: Record<string, unknown>): CalendarSearch {
  const list = parseListSearch(raw);
  const filters = filtersOf(list);
  delete filters.due;
  delete filters.dueFrom;
  delete filters.dueTo;
  const search: CalendarSearch = filters;
  if (raw.view === 'week') search.view = 'week';
  const date = isoDateSchema.safeParse(raw.date);
  if (date.success) search.date = date.data;
  if (list.task !== undefined) search.task = list.task;
  return search;
}

/** Filters for the task list query (without view, date and open task). */
export function calendarFilters(search: CalendarSearch): TaskListFilter {
  const filters = { ...search };
  delete filters.view;
  delete filters.date;
  delete filters.task;
  return filters;
}

export function withCalendarTask(search: CalendarSearch, key: string | undefined): CalendarSearch {
  const next = { ...search };
  if (key === undefined) delete next.task;
  else next.task = key;
  return next;
}

/**
 * Done tasks are left out of the calendar, unless the status filter names statuses explicitly
 * (then the selection decides, e.g. "Erledigt" to look back).
 */
export function shownInCalendar(
  task: Pick<TaskSummary, 'status'>,
  filters: Pick<TaskListFilter, 'status'>,
): boolean {
  return filters.status !== undefined || task.status.category !== 'done';
}
